import {useState} from 'react'
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  subMonths,
  isSameMonth,
  isSameDay,
  isToday,
} from 'date-fns'
import {useBirthdays} from '../../hooks/useBirthdays'
import {BirthdayIcon} from './BirthdayIcon'
import {BirthdayListItem} from './BirthdayListItem'
import {BirthdayModal} from './BirthdayModal'
import {BirthdayMember} from '../../types/birthdays'
import {ModalPreview} from '../contacts/components/ModalPreview'
import './CalendarStyles.css'

type ViewMode = 'month' | 'week'

export const CalendarPage = () => {
  const [currentDate, setCurrentDate] = useState(new Date())
  const [viewMode, setViewMode] = useState<ViewMode>('month')
  const [selectedDayBirthdays, setSelectedDayBirthdays] = useState<{
    birthdays: BirthdayMember[]
    date: Date
  } | null>(null)
  const [previewContactId, setPreviewContactId] = useState<string | null>(null)
  const [loadingDay, setLoadingDay] = useState<number | null>(null)
  const [loadingDayKey, setLoadingDayKey] = useState<string | null>(null)
  const [expandedDays, setExpandedDays] = useState<Record<string, boolean>>({})
  const [dayOverrides, setDayOverrides] = useState<Record<string, BirthdayMember[]>>({})

  // Obtener el mes del currentDate para pasarlo al hook
  const currentMonth = currentDate.getMonth() + 1 // getMonth() retorna 0-11, necesitamos 1-12

  // Obtener cumpleaños de contactos del mes actual
  const {birthdaysByDate, loading, isPartial, loadDayBirthdays} = useBirthdays(currentMonth)

  const openPreview = (contactId: string) => setPreviewContactId(contactId)
  const closePreview = () => setPreviewContactId(null)

  const getDayKey = (date: Date) => format(date, 'MM-dd')

  const getBirthdaysForDate = (date: Date) => {
    const monthDay = getDayKey(date)
    return dayOverrides[monthDay] || birthdaysByDate[monthDay] || []
  }

  const handleShowMore = async (date: Date, dayBirthdays: BirthdayMember[]) => {
    const dayNumber = date.getDate()
    if (isPartial) {
      try {
        setLoadingDay(dayNumber)
        const fullDay = await loadDayBirthdays(dayNumber)
        setSelectedDayBirthdays({birthdays: fullDay, date})
      } catch (e) {
        // Silently ignore; could add toast if desired
        setSelectedDayBirthdays({birthdays: dayBirthdays, date})
      } finally {
        setLoadingDay(null)
      }
      return
    }
    setSelectedDayBirthdays({birthdays: dayBirthdays, date})
  }

  const handleWeekShowMore = async (date: Date, dayBirthdays: BirthdayMember[]) => {
    const dayKey = getDayKey(date)
    if (expandedDays[dayKey]) return

    if (isPartial && !dayOverrides[dayKey]) {
      try {
        setLoadingDayKey(dayKey)
        const fullDay = await loadDayBirthdays(date.getDate())
        setDayOverrides((prev) => ({...prev, [dayKey]: fullDay}))
        setExpandedDays((prev) => ({...prev, [dayKey]: true}))
      } catch (e) {
        setExpandedDays((prev) => ({...prev, [dayKey]: true}))
      } finally {
        setLoadingDayKey(null)
      }
      return
    }

    setExpandedDays((prev) => ({...prev, [dayKey]: true}))
  }

  const renderMonthView = () => {
    const monthStart = startOfMonth(currentDate)
    const monthEnd = endOfMonth(monthStart)
    const startDate = startOfWeek(monthStart)
    const endDate = endOfWeek(monthEnd)

    const rows: JSX.Element[] = []
    let days: JSX.Element[] = []
    let day = startDate

    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        const cloneDay = day
        const dayBirthdays = getBirthdaysForDate(cloneDay)
        const visibleLimit = 5
        const visibleBirthdays = dayBirthdays.slice(0, visibleLimit)
        const remainingCount = dayBirthdays.length - visibleLimit

        days.push(
          <div
            key={day.toString()}
            className={`calendar-day ${loading ? 'loading' : ''} ${
              !isSameMonth(day, monthStart) ? 'disabled' : ''
            } ${isSameDay(day, new Date()) ? 'today' : ''}`}
          >
            <span className='day-number'>{format(day, 'd')}</span>
            {loading ? (
              <div className='day-loading'>
                <div className='skeleton-line long' />
                <div className='skeleton-line short' />
              </div>
            ) : (
              dayBirthdays.length > 0 && (
                <div className='birthday-icons-container'>
                  {visibleBirthdays.map((birthday) => (
                    <BirthdayIcon
                      key={birthday.id}
                      birthday={birthday}
                      onOpenPreview={openPreview}
                    />
                  ))}
                  {(remainingCount > 0 || (isPartial && dayBirthdays.length > 0)) && (
                    <button
                      className='birthday-more-button'
                      onClick={() => handleShowMore(cloneDay, dayBirthdays)}
                      disabled={loadingDay === cloneDay.getDate()}
                    >
                      {loadingDay === cloneDay.getDate()
                        ? 'Loading...'
                        : isPartial
                        ? 'Show more'
                        : `+${remainingCount} more`}
                    </button>
                  )}
                </div>
              )
            )}
          </div>
        )
        day = addDays(day, 1)
      }
      rows.push(
        <div className='calendar-row' key={day.toString()}>
          {days}
        </div>
      )
      days = []
    }

    return <div className='calendar-body'>{rows}</div>
  }

  const renderWeekView = () => {
    const weekStart = startOfWeek(currentDate)
    const days: JSX.Element[] = []

    for (let i = 0; i < 7; i++) {
      const day = addDays(weekStart, i)
      const dayBirthdays = getBirthdaysForDate(day)
      const dayKey = getDayKey(day)
      const visibleLimit = 5
      const isExpanded = !isPartial || !!expandedDays[dayKey]
      const needsShowMore = isPartial && !isExpanded && dayBirthdays.length > 0
      const visibleList =
        !isPartial || isExpanded ? dayBirthdays : dayBirthdays.slice(0, visibleLimit)

      days.push(
        <div key={i} className='week-list-item'>
          <div className='week-day-header'>
            <span className='week-day-name'>{format(day, 'EEEE')}</span>
            <span className={`week-day-number ${isToday(day) ? 'today' : ''}`}>
              {format(day, 'MMM d, yyyy')}
            </span>
          </div>
          <div className={`week-day-content ${isExpanded ? 'expanded' : ''}`}>
            {loading ? (
              <div className='day-loading'>
                <div className='skeleton-line long' />
                <div className='skeleton-line short' />
              </div>
            ) : dayBirthdays.length > 0 ? (
              <>
                <div className={`birthdays-list ${isExpanded ? 'week-day-scroll' : ''}`}>
                  {visibleList.map((birthday) => (
                    <BirthdayListItem
                      key={birthday.id}
                      birthday={birthday}
                      onOpenPreview={openPreview}
                    />
                  ))}
                </div>
                {needsShowMore && (
                  <button
                    className='birthday-more-button mt-2'
                    onClick={() => handleWeekShowMore(day, dayBirthdays)}
                    disabled={loadingDayKey === dayKey}
                  >
                    {loadingDayKey === dayKey
                      ? 'Loading...'
                      : isPartial
                      ? 'Show more'
                      : `+${dayBirthdays.length - visibleList.length} more`}
                  </button>
                )}
              </>
            ) : (
              <div className='no-events'>No birthdays</div>
            )}
          </div>
        </div>
      )
    }

    return <div className='week-list-view'>{days}</div>
  }

  const nextPeriod = () => {
    if (viewMode === 'month') {
      setCurrentDate(addMonths(currentDate, 1))
    } else {
      setCurrentDate(addDays(currentDate, 7))
    }
  }

  const prevPeriod = () => {
    if (viewMode === 'month') {
      setCurrentDate(subMonths(currentDate, 1))
    } else {
      setCurrentDate(addDays(currentDate, -7))
    }
  }

  const goToToday = () => {
    setCurrentDate(new Date())
  }

  const renderHeader = () => {
    return (
      <div className='calendar-header'>
        <div className='header-left'>
          <h2>{format(currentDate, 'MMMM yyyy')}</h2>
        </div>
        <div className='header-right'>
          <button className='btn btn-sm btn-light me-2' onClick={goToToday}>
            Today
          </button>
          <button className='btn btn-sm btn-icon btn-light me-2' onClick={prevPeriod}>
            <i className='bi bi-chevron-left'></i>
          </button>
          <button className='btn btn-sm btn-icon btn-light me-3' onClick={nextPeriod}>
            <i className='bi bi-chevron-right'></i>
          </button>
          <div className='btn-group'>
            <button
              className={`btn btn-sm ${viewMode === 'month' ? 'btn-primary' : 'btn-light'}`}
              onClick={() => setViewMode('month')}
            >
              Month
            </button>
            <button
              className={`btn btn-sm ${viewMode === 'week' ? 'btn-primary' : 'btn-light'}`}
              onClick={() => setViewMode('week')}
            >
              Week List
            </button>
          </div>
        </div>
      </div>
    )
  }

  const renderDaysOfWeek = () => {
    const days: JSX.Element[] = []
    const startDate = startOfWeek(currentDate)

    for (let i = 0; i < 7; i++) {
      days.push(
        <div className='calendar-day-name' key={i}>
          {format(addDays(startDate, i), 'EEE')}
        </div>
      )
    }

    return <div className='calendar-days-of-week'>{days}</div>
  }

  return (
    <div className='calendar-page'>
        <div className='card'>
          <div className='card-body p-5'>
          {renderHeader()}
          {viewMode === 'month' && (
            <>
              {renderDaysOfWeek()}
              {renderMonthView()}
            </>
          )}
          {viewMode === 'week' && renderWeekView()}
        </div>
      </div>
      {selectedDayBirthdays && (
        <BirthdayModal
          birthdays={selectedDayBirthdays.birthdays}
          date={selectedDayBirthdays.date}
          onClose={() => setSelectedDayBirthdays(null)}
          onOpenPreview={openPreview}
        />
      )}
      {previewContactId && (
        <ModalPreview idContact={previewContactId} onClose={closePreview} />
      )}
    </div>
  )
}
